/** What `qa/ux_survey.py` reads. One object on `window`, written from two places.
 *
 * ⚠⚠ **THIS EXISTS BECAUSE THE THINGS THAT MAKE A CAD PROGRAM FEEL FAMILIAR ARE INVISIBLE TO
 * EVERY OTHER INSTRUMENT IN `qa/`.** A selection window is BLUE and solid, a crossing is GREEN
 * and dashed; the drawing that comes out is identical either way, so the render gate, the e2e
 * suite and the command survey all score them the same. A drafter does not — the colour is the
 * only feedback distinguishing two gestures that differ solely in direction, and they read it
 * without looking.
 *
 * ⚠ Read-only, and deliberately: nothing in the app may branch on it. A probe the product
 * depends on stops being a probe and becomes an API, and then the survey is measuring an
 * arrangement that exists to be measured.
 *
 * ⚠ A `band` is only published while a drag is LIVE, which is the whole point — a screenshot
 * cannot be taken in the middle of a mouse-down, so the colour and the dash of the two bands
 * are otherwise unobservable from outside.
 */
export interface UxProbe {
  /** The view transform, as the canvas holds it. */
  view?: { x: number; y: number; scale: number; rot?: number };
  /** The model point under the pointer, computed by the canvas's OWN `toModel` — so a check for
   *  "the point under the cursor stayed put while zooming" cannot pass by re-deriving it with a
   *  transform that agrees with itself. */
  cursorModel?: { x: number; y: number } | null;
  /** The live selection band. `mode` is the thing a drafter reads off the colour. */
  band?: { mode: "window" | "crossing"; color: string; dash: boolean } | null;
  /** How many grips are drawn, and where the first one is, in screen pixels. */
  grips?: number;
  gripPt?: { x: number; y: number } | null;
  /** Which grip is HOT — AutoCAD turns it red. `-1` for none. */
  hotGrip?: number;
  /** Which grip the cursor is OVER — AutoCAD's rollover, and a different state from hot. ⚠ Kept
   *  separate on purpose: one field for both would make this probe unable to tell a grip that
   *  lights under the cursor from one that has been clicked. */
  hoverGrip?: number;
  /** The armed object snap, if the cursor is near one. */
  snap?: { kind: string; x: number; y: number } | null;
  /** How many objects are selected, and a screen point on one of them. */
  sel?: number;
  selPt?: { x: number; y: number } | null;
  /** The status-bar toggles, by name — ORTHO, OSNAP, OTRACK, DYNMODE and the rest. */
  tog?: Record<string, boolean>;
  /** The grip edit mode a hot grip is in: STRETCH, MOVE, ROTATE, SCALE, MIRROR. */
  gripMode?: string | null;
  /** The command the app would repeat on ENTER at an empty prompt. */
  lastCommand?: string | null;
  /** How many times the canvas has painted since the page loaded.
   *
   * ⚠⚠ **A CANVAS THAT REPAINTS FOREVER IS INVISIBLE FROM OUTSIDE AND RUINS EVERYTHING.** It
   * looks like a correct drawing; the only symptom is a fan and a survey that takes twice as
   * long, which reads as "this machine is busy" rather than as a defect. It cost an hour of
   * chasing `ps` output. One counter turns it into a number any check can assert on: paint
   * twice with nothing happening and something is wrong. */
  paints?: number;
  /** Which SPACE is on screen — `null` for model, the layout's name for a sheet.
   *
   * ⚠⚠ **THE APP'S OWN ANSWER TO "WHAT AM I LOOKING AT", WHICH NOTHING COULD ASK BEFORE.** A
   * layout tab lighting up is not the same as a sheet being drawn, and for one afternoon a stub
   * was rendered in front of a working sheet on the strength of a screenshot taken while the
   * compose was still in flight. A check that reads this cannot make that mistake twice. */
  space?: string | null;
}

declare global {
  interface Window { __pdcadUX?: UxProbe }
}

/** Merge a patch into the probe. ⚠ Merge rather than replace: the canvas and the studio each
 *  publish the half they can see, and a replace would make whichever ran last the only one. */
export function publishUX(patch: UxProbe): void {
  if (typeof window === "undefined") return;
  window.__pdcadUX = { ...(window.__pdcadUX || {}), ...patch };
}
